import React, { useEffect, useRef, useState } from "react";
import {
  Pagination,
  Select,
  Skeleton,
  Text,
  Modal,
  Button,
  Paper,
  Group,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import { useNavigate, useLocation } from "react-router-dom";
import TemplateRow from "./TemplateRow";
import {
  formatDate,
  formatTime,
  getValueForInput,
  loadingStates,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import colors from "../../utilities/design";
import Editor from "../Editor";
import styles from "./TemplateRow.module.css";

import {
  templatesDetailsApi,
  templatesListApi,
  deleteTemplate,
} from "../../utilities/apis/templates";
import { selectUserData } from "../../redux/selectors";
import { useSelector } from "react-redux";
import { getDocsToken } from "../../utilities/apis/agreements";
import constants from "../../utilities/constants";
import { FileDownload } from "tabler-icons-react";

function TemplatesList({
  useTemplateFunc,
  templateType = "Notices",
  showNewTemplateBtn = true,
  showEditButton = false,
  showViewButton = true,
  placeholders,
  setPlaceholders,
}) {
  const arr = useLocation().pathname.split("/");
  let type = arr[arr.length - 1];
  const currentUser = useSelector(selectUserData) || {};

  const [templatesConfig, setTemplatesConfig] = useState({
    loading: loadingStates.LOADING,
    templates: null,
    page: 1,
    templatesCount: null,
    templateType,
    templateCategory: "All",
    templateCategoriesList: ["All"],
    templateToView: null,
    templateToViewData: null,
    templateToViewLoading: loadingStates.NO_ACTIVE_REQUEST,
  });
  const [sfdtContent, setSfdtContent] = useState(null);
  const editorRef = useRef();
  const displayType =
    templateType === "Agreements" ? "Contracts" : templateType;

  const getTemplateDetails = async (templateId) => {
    setTemplatesConfig((prevState) => ({
      ...prevState,
      templateToViewLoading: loadingStates.LOADING,
      templateToViewData: null,
    }));
    const response = await apiWrapWithErrorWithData(
      templatesDetailsApi({
        templateId,
      }),
    );
    if (response?.success && response?.template) {
      setTemplatesConfig({
        ...templatesConfig,
        templateToViewLoading: loadingStates.NO_ACTIVE_REQUEST,
        templateToViewData: response.template,
      });
      setPlaceholders(response.template.fieldName || []);
      const accessToken = await apiWrapWithErrorWithData(getDocsToken());

      const response2 = await fetch(
        `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/items/${response.template.quillDelta}/content`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );

      if (response2.headers.get("Content-Length")) {
        const blob = await response2.blob();
        const file = new File([blob], "doc.docx", {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        const formData = new FormData();
        formData.append("files", file);

        const sfdtResponse = await fetch(
          `${constants.wordeditorApi}/api/documenteditor/Import`,
          { method: "POST", body: formData },
        );

        const sfdt = await sfdtResponse.json();
        setSfdtContent(sfdt);
      }
    } else {
      // showNotification({
      //   color: "red",
      //   title: "Templates Details",
      //   message: "Failed to load template details.",
      // });
      setTemplatesConfig({
        ...templatesConfig,
        templateToViewLoading: loadingStates.NO_ACTIVE_REQUEST,
      });
    }
  };

  const getTemplatesList = async () => {
    setTemplatesConfig((prevState) => ({
      ...prevState,
      loading: loadingStates.LOADING,
      templatesCount: null,
    }));
    const response = await apiWrapWithErrorWithData(
      templatesListApi({
        page: templatesConfig.page,
        type: templatesConfig.templateType,
        category:
          templatesConfig.templateCategory.toLowerCase() === "all"
            ? null
            : templatesConfig.templateCategory,
      }),
    );
    if (response?.success && response?.templates) {
      setTemplatesConfig({
        ...templatesConfig,
        templates: response.templates,
        loading: loadingStates.NO_ACTIVE_REQUEST,
        templatesCount: response.templatesCount || null,
        templateCategoriesList: [
          "All",
          ...response.templateCategories.map((template) => template.category),
        ],
      });
    } else {
      // showNotification({
      //   color: "red",
      //   title: "Templates List",
      //   message: "Failed to load templates list",
      // });
      setTemplatesConfig({
        ...templatesConfig,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    }
  };
  useEffect(() => {
    getTemplatesList();
  }, [
    templatesConfig.page,
    templatesConfig.templateCategory,
    templatesConfig.templateType,
  ]);

  useEffect(() => {
    setTemplatesConfig((preState) => ({
      ...preState,
      templateType: templateType,
    }));
  }, [templateType]);

  useEffect(() => {
    if (templatesConfig.templateToView?.id) {
      getTemplateDetails(templatesConfig.templateToView.id);
    }
  }, [templatesConfig.templateToView]);

  const viewHandler = (template) => {
    setTemplatesConfig((prevState) => ({
      ...prevState,
      templateToView: template,
    }));
  };

  const templateDetails = templatesConfig.templateToViewData;

  const navigate = useNavigate();

  const useTemplateHandler = useTemplateFunc
    ? () => {
        setTemplatesConfig((prevState) => ({
          ...prevState,
          templateToView: null,
          templateToViewData: null,
        }));
        useTemplateFunc(templatesConfig.templateToViewData);
      }
    : null;

  const deleteHandler = async (id) => {
    try {
      const response = await apiWrapWithErrorWithData(
        deleteTemplate({ templateId: id }),
      );
      // console.log({ templateDelRes: response });
      if (response.success) {
        showNotification({
          color: "green",
          title: "Templates",
          message: "Template is deleted",
        });
      } else {
        showNotification({
          color: "red",
          title: "Templates",
          message: "Unable to delete the template",
        });
      }
      getTemplatesList();
    } catch (err) {
      console.log({ err });
      showNotification({
        color: "red",
        title: "Templates",
        message: "Unable to delete the template",
      });
      getTemplatesList();
    }
  };

  // const getTemplate = async ()=>{
  //   const response = await apiWrapWithErrorWithData(templatesDetailsApi({templateId: templatesConfig.templateToView}))
  // }
  return (
    <>
      {templatesConfig.templateToView && (
        <Modal
          overflow="inside"
          opened
          onClose={() => {
            setTemplatesConfig((prevState) => ({
              ...prevState,
              templateToView: null,
              templateToViewQuillDelta: null,
            }));
          }}
          centered
          size="95vw"
          radius="lg"
          styles={{
            header: {
              display: "none",
            },

            body: {
              padding: 0,
              height: "92vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },

            content: {
              width: "95vw",
              maxWidth: "95vw",
              height: "92vh",
              maxHeight: "92vh",
              overflow: "hidden",
              borderRadius: 18,
            },
          }}
          withCloseButton
        >
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {templatesConfig.templateToViewLoading ===
              loadingStates.LOADING && (
              <BeatLoader color={colors.primary} size={10} />
            )}
            {templatesConfig.templateToViewLoading !== loadingStates.LOADING &&
              !!templateDetails && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Paper
                    radius="lg"
                    shadow="sm"
                    withBorder
                    px="lg"
                    py="sm"
                    style={{
                      background: "#fff",
                      border: "1px solid #E9ECEF",
                      transition: "0.2s",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingBottom: 10,
                      }}
                    >
                      <div className="flex flex-row">
                        <div>
                          <FileDownload
                            size={32}
                            strokeWidth={1.7}
                            color="#1971C2"
                          />
                        </div>
                        <div className="flex flex-col pl-3">
                          <Text fw={600} size="md">
                            {templateDetails.name || templateDetails.title}
                          </Text>
                          <div className="flex flex-row text-center ">
                            <div className={`${styles.text} mr-3`}>
                              Created By {templateDetails.createdBy.name}
                            </div>
                            <div>
                              <img
                                src="/assets/images/clock2.svg"
                                alt="clock"
                              />
                            </div>
                            <div className={`${styles.date} ml-2`}>
                              {formatDate(templateDetails.createdAt)},
                              {formatTime(templateDetails.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <Text
                        fw={600}
                        c="dimmed"
                        style={{
                          padding: "10px 12px",
                          borderRadius: 20,
                          background: "#F1F5F9",
                        }}
                      >
                        {templateDetails.category}
                      </Text>
                    </div>
                  </Paper>

                  <Group justify="space-between" mb="sm" mt="md">
                    <div>
                      <Title order={5}>Template Preview</Title>
                      <Text size="sm" c="dimmed">
                        Review template before using it.
                      </Text>
                    </div>
                    {!!useTemplateHandler && (
                      <Button radius="md" onClick={useTemplateHandler}>
                        Use Template
                      </Button>
                    )}
                  </Group>

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      overflow: "hidden",
                      minHeight: 0,
                      height: "100%",
                      borderRadius: 12,
                    }}
                  >
                    <Paper
                      withBorder
                      radius="lg"
                      shadow="xs"
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        display: "flex",
                      }}
                    >
                      <Editor
                        placeholders={placeholders}
                        setPlaceholders={setPlaceholders}
                        disabled={true}
                        ref={editorRef}
                        initialContent={sfdtContent}
                        editNotAllowed={true}
                      />
                    </Paper>
                  </div>
                </div>
              )}
          </div>
        </Modal>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: 24,
          height: "100%",
        }}
      >
        <Group justify="space-between" align="flex-end" mb={24} mt={8}>
          <Select
            disabled={templatesConfig.loading === loadingStates.LOADING}
            label="Category"
            w={250}
            radius="md"
            data={templatesConfig.templateCategoriesList}
            value={templatesConfig.templateCategory || "All"}
            onChange={(input) => {
              const val = getValueForInput(input);
              setTemplatesConfig((prevState) => ({
                ...prevState,
                templateCategory: val,
              }));
            }}
          />
          {showNewTemplateBtn &&
          (currentUser.loginAs.id === 4 ||
            currentUser.loginAs.id === 1 ||
            currentUser.loginAs.id === 2) ? (
            <Button
              onClick={() => {
                // type === "agreement"
                navigate("/app/templates/agreements_manager/agreements/new");
              }}
              // style={{
              //   backgroundColor: "#46BDE1",
              // }}
            >
              Add New Template
            </Button>
          ) : null}
        </Group>
        <div
          style={{
            overflowY: "auto",
            maxHeight: "55vh",
          }}
        >
          {templatesConfig.loading === loadingStates.LOADING && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "82vh",
              }}
            >
              <Skeleton height={40} className="mb-11" />
              <Skeleton height={40} className="mb-11" />
              <Skeleton height={40} className="mb-11" />
              <Skeleton height={40} className="mb-11" />
              <Skeleton height={40} className="mb-11" />
            </div>
          )}
          {templatesConfig.loading !== loadingStates.LOADING &&
            templatesConfig.templates.map((template) => (
              <div
                key={template.id}
                style={{
                  marginBottom: 12,
                }}
              >
                <Paper
                  radius="lg"
                  shadow="sm"
                  withBorder
                  px="lg"
                  py="sm"
                  style={{
                    background: "#fff",
                    border: "1px solid #E9ECEF",
                    transition: "0.2s",
                  }}
                >
                  <TemplateRow
                    editButton={showEditButton}
                    viewButton={showViewButton}
                    onDelete={deleteHandler}
                    onViewClick={() => {
                      viewHandler(template);
                    }}
                    {...template}
                  />
                </Paper>
              </div>
            ))}
          {templatesConfig.templates?.length === 0 && (
            <div className="my-4 flex justify-center">
              <Text>No Templates Available</Text>
            </div>
          )}
        </div>
        {templatesConfig.templatesCount && (
          <Paper
            withBorder
            radius="md"
            p="sm"
            mt="lg"
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Pagination
              // total={3}
              size="sm"
              // color="cyan"
              onChange={(page) => {
                setTemplatesConfig({
                  ...templatesConfig,
                  page,
                });
              }}
              total={Math.ceil(templatesConfig.templatesCount / 10)}
              page={templatesConfig.page}
            />
          </Paper>
        )}
      </div>
    </>
  );
}

export default TemplatesList;
